import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './IncluirEntradaAntecipada.css';

/**
 * Página para Admin adicionar entrada antecipada
 */

const IncluirEntradaAntecipada = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadaAberta, setRodadaAberta] = useState(null);
  const [jogadoresConfirmados, setJogadoresConfirmados] = useState([]);
  const [jogadoresComEntradaAntecipada, setJogadoresComEntradaAntecipada] = useState([]);
  const [jogadoresSelecionados, setJogadoresSelecionados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [estornando, setEstornando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      const rodadasRes = await apiClient.get('/rodadas');
      const rodadas = Array.isArray(rodadasRes.data) ? rodadasRes.data : [];
      const aberta = rodadas.find(r => r.status === 'aberta');

      if (!aberta) {
        setError('Não há rodada aberta. Abra uma rodada primeiro.');
        setLoading(false);
        return;
      }

      setRodadaAberta(aberta);

      // Carregar confirmações
      const confirmacoesRes = await apiClient.get(`/rodadas/${aberta.id}/confirmacoes`);
      const confirmacoes = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
      // Separar jogadores com e sem entrada antecipada
      const jogadoresSemEntradaAntecipada = confirmacoes.filter(c => !c.entrada_antecipada);
      const jogadoresComEntrada = confirmacoes.filter(c => c.entrada_antecipada);
      setJogadoresConfirmados(jogadoresSemEntradaAntecipada);
      setJogadoresComEntradaAntecipada(jogadoresComEntrada);

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleJogador = (jogadorId) => {
    setJogadoresSelecionados(prev => {
      if (prev.includes(jogadorId)) {
        return prev.filter(id => id !== jogadorId);
      } else {
        return [...prev, jogadorId];
      }
    });
    setError('');
    setSuccess('');
  };

  const handleSelecionarTodos = () => {
    if (jogadoresSelecionados.length === jogadoresConfirmados.length) {
      setJogadoresSelecionados([]);
    } else {
      setJogadoresSelecionados(jogadoresConfirmados.map(c => c.jogador_id));
    }
    setError('');
    setSuccess('');
  };

  const handleAdicionarEntradaAntecipada = async (e) => {
    e.preventDefault();
    
    if (jogadoresSelecionados.length === 0) {
      setError('Selecione pelo menos um jogador');
      return;
    }

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      const response = await apiClient.post(`/rodadas/${rodadaAberta.id}/entrada-antecipada`, {
        jogadores_ids: jogadoresSelecionados,
      });

      // Mostrar resultado
      const sucessoCount = response.data.sucesso?.length || 0;
      const errosCount = response.data.erros?.length || 0;
      
      let mensagem = `${sucessoCount} entrada(s) antecipada(s) adicionada(s) com sucesso!`;
      if (errosCount > 0) {
        mensagem += `\n${errosCount} jogador(es) não puderam ser processados.`;
        const errosDetalhes = response.data.erros.map(e => 
          `- ${e.jogador_nome || e.jogador_id}: ${e.erro}`
        ).join('\n');
        mensagem += '\n\nDetalhes:\n' + errosDetalhes;
      }
      
      alert(mensagem);
      
      setSuccess(response.data.message);
      setJogadoresSelecionados([]);
      
      // Recarregar dados para atualizar lista
      await carregarDados();
    } catch (err) {
      console.error('Erro ao adicionar entrada antecipada:', err);
      setError(err.response?.data?.error || 'Erro ao adicionar entrada antecipada');
      if (err.response?.data?.details) {
        setError(err.response.data.error + '\n' + JSON.stringify(err.response.data.details, null, 2));
      }
    } finally {
      setSalvando(false);
    }
  };

  const handleEstornarEntradaAntecipada = async (jogadorId, jogadorNome) => {
    if (!window.confirm(`Deseja estornar a entrada antecipada de ${jogadorNome}?`)) {
      return;
    }

    try {
      setEstornando(true);
      setError('');
      setSuccess('');

      await apiClient.delete(`/rodadas/${rodadaAberta.id}/entrada-antecipada`, {
        data: { jogador_id: jogadorId },
      });

      alert('Entrada antecipada estornada com sucesso!');
      setSuccess('Entrada antecipada estornada com sucesso');
      
      // Recarregar dados para atualizar lista
      await carregarDados();
    } catch (err) {
      console.error('Erro ao estornar entrada antecipada:', err);
      setError(err.response?.data?.error || 'Erro ao estornar entrada antecipada');
    } finally {
      setEstornando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodadaAberta) {
    return (
      <div className="container">
        <div className="error">Não há rodada aberta. Abra uma rodada primeiro.</div>
        <button onClick={() => navigate('/rodadas')} className="btn btn-primary">
          Ir para Rodadas
        </button>
      </div>
    );
  }

  return (
    <div className="container incluir-entrada-antecipada-container">
      <div className="page-header">
        <h1>💰 Incluir Entrada Antecipada</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodadaAberta.data).toLocaleDateString('pt-BR')}</p>
        <p><strong>Valor:</strong> R$ 40,00</p>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <form onSubmit={handleAdicionarEntradaAntecipada} className="entrada-antecipada-form">
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label>Jogadores *</label>
            <button 
              type="button" 
              onClick={handleSelecionarTodos}
              className="btn btn-secondary"
              style={{ padding: '5px 15px', fontSize: '14px' }}
            >
              {jogadoresSelecionados.length === jogadoresConfirmados.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
            </button>
          </div>
          
          {jogadoresConfirmados.length > 0 ? (
            <div className="jogadores-checklist">
              {jogadoresConfirmados.map(c => (
                <label key={c.jogador_id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={jogadoresSelecionados.includes(c.jogador_id)}
                    onChange={() => handleToggleJogador(c.jogador_id)}
                  />
                  <span>{c.jogador?.nome || 'Jogador'}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="no-jogadores">
              <p>Nenhum jogador disponível (todos já têm entrada antecipada)</p>
            </div>
          )}
          
          <small>
            {jogadoresSelecionados.length > 0 
              ? `${jogadoresSelecionados.length} jogador(es) selecionado(s)` 
              : 'Selecione um ou mais jogadores confirmados na rodada'}
          </small>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            disabled={salvando || jogadoresSelecionados.length === 0} 
            className="btn btn-primary"
          >
            {salvando 
              ? `Adicionando ${jogadoresSelecionados.length} entrada(s)...` 
              : `Adicionar ${jogadoresSelecionados.length > 0 ? jogadoresSelecionados.length : ''} Entrada(s) Antecipada(s)`}
          </button>
        </div>
      </form>

      {/* Lista de jogadores com entrada antecipada (para estornar) */}
      {jogadoresComEntradaAntecipada.length > 0 && (
        <div className="jogadores-com-entrada-section" style={{ marginTop: '30px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
          <h2>Jogadores com Entrada Antecipada ({jogadoresComEntradaAntecipada.length})</h2>
          <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>
            Clique no ícone de lixeira para estornar a entrada antecipada de um jogador.
          </p>
          <div className="jogadores-com-entrada-list">
            {jogadoresComEntradaAntecipada.map(c => (
              <div key={c.jogador_id} className="jogador-com-entrada-item" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px 15px',
                marginBottom: '10px',
                background: '#fff',
                borderRadius: '5px',
                border: '1px solid #ddd'
              }}>
                <div>
                  <strong>{c.jogador?.nome || 'Jogador'}</strong>
                  <span style={{ marginLeft: '10px', color: '#4caf50', fontSize: '0.9em' }}>✓ Entrada Antecipada</span>
                </div>
                <button
                  onClick={() => handleEstornarEntradaAntecipada(c.jogador_id, c.jogador?.nome)}
                  disabled={estornando}
                  className="btn btn-danger"
                  style={{ 
                    padding: '5px 15px', 
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                  title="Estornar entrada antecipada"
                >
                  🗑️ {estornando ? 'Estornando...' : 'Estornar'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default IncluirEntradaAntecipada;
